import React from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { USD } from 'helpers/formatters/number_formats';

import type { PurchaseTransaction } from 'components/users/types';

interface Props {
  transactions: PurchaseTransaction[];
}

export default function PurchasesTab({ transactions }: Props) {
  return (
    <>
      <h5 className="mb-2">Recent Purchases</h5>

      {transactions.map(transaction => (
        <Card className="mb-3" key={transaction.referenceNumber}>
          <Collapsible>
            <Collapsible.Trigger render={<Card.Header className="clickable" />}>
              <div className="row align-items-center">
                <div className="col-9 text-start">
                  <div>
                    <strong>Date: </strong>
                    {transaction.date}
                  </div>
                  <div>
                    <strong>Reference Number: </strong>
                    {transaction.referenceNumber}
                  </div>
                </div>
                <div className="col-3 text-end">
                  <div>Total:</div>
                  <div>
                    <strong>{USD.format(transaction.total)}</strong>
                  </div>
                </div>
              </div>
            </Collapsible.Trigger>

            <Collapsible.Content>
              <Card.Body>
                {transaction.purchaseLink && (
                  <div className="mb-2">
                    View purchase:{' '}
                    <strong>
                      <a href={transaction.purchaseLink.path}>{transaction.purchaseLink.name}</a>
                    </strong>
                  </div>
                )}

                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>Product</Table.Head>
                      <Table.Head>Description</Table.Head>
                      <Table.Head className="text-end">Price</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {transaction.lineItems.map((lineItem, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>{lineItem.product}</Table.Cell>
                        <Table.Cell>{lineItem.description}</Table.Cell>
                        <Table.Cell className="text-end">
                          <strong>{USD.format(lineItem.price)}</strong>
                        </Table.Cell>
                      </Table.Row>
                    ))}

                    {transaction.discount && (
                      <Table.Row>
                        <Table.Cell>Discount code &quot;{transaction.discount.code}&quot;</Table.Cell>
                        <Table.Cell>{transaction.discount.description}</Table.Cell>
                        <Table.Cell className="text-end">
                          <strong>-{USD.format(transaction.discountAmount)}</strong>
                        </Table.Cell>
                      </Table.Row>
                    )}

                    <Table.Row>
                      <Table.Cell colSpan={3}>
                        <div className="d-flex justify-content-end">
                          <div className="text-end pe-3">
                            <div>Subtotal:</div>
                            <div>Tax:</div>
                            <div>Total:</div>
                          </div>
                          <div className="text-end">
                            <div>
                              <strong>{USD.format(transaction.subtotal - transaction.discountAmount)}</strong>
                            </div>
                            <div>
                              <strong>{USD.format(transaction.tax)}</strong>
                            </div>
                            <div>
                              <strong>{USD.format(transaction.total)}</strong>
                            </div>
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>

                <div className="text-end">
                  <a href={transaction.paths.invoice}>Download Invoice</a>
                </div>
              </Card.Body>
            </Collapsible.Content>
          </Collapsible>
        </Card>
      ))}
    </>
  );
}
